/* eslint-disable react-hooks/exhaustive-deps */
import { CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { StatusCode } from '../../api/enumerations';
import { otpSend, otpVerify, totpVerify } from '../../api/mfa';
import { MfaLoginProps } from '../../api/mfa/types';
import OTPInput from '../../components/OTPInput';
import { IconTimerMS } from '../../constants/icons';
import { Constants } from '../../constants/mfa';
import { MfaMode } from '../../helpers/mfa';
import useErrorMessage from '../../hooks/useErrorMessage';
import {
  ButtonBox,
  Counter,
  FilledButton,
  NewCodeButton,
  OutlinedStyledButton,
  SectionDialog,
  Span,
  Title,
} from './styles';

interface VerifyCodeProps {
  /** 'totp' reads the code from the app; 'otp' from the SMS/e-mail message. */
  mode: Exclude<MfaMode, 'setup'>;
  token: string;
  onAuthenticated: (payload: MfaLoginProps) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}

/**
 * Code challenge of a login that came back MFA-pending. Only the OTP mode can
 * resend a code: TOTP is generated by the app, there is nothing to send.
 */
export function VerifyCode({
  mode,
  token,
  onAuthenticated,
  onCancel,
  onError,
}: VerifyCodeProps): JSX.Element {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(Constants.otpResendCooldown);

  const { getErrorMessage } = useErrorMessage();

  const isOtp = mode === 'otp';

  useEffect(() => {
    // The API already sent the first code with the login; this only counts the
    // window down before the user may ask for another one.
    if (!isOtp || timeLeft <= 0) return undefined;

    const interval = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [isOtp, timeLeft]);

  const handleResend = useCallback(async (): Promise<void> => {
    setCode('');
    setTimeLeft(Constants.otpResendCooldown);

    try {
      const { detail } = await otpSend({ token });

      if (detail.description) throw new Error(detail.description);
      if (detail.status_code !== StatusCode.OK)
        throw new Error(Constants.genericError);
    } catch (error) {
      onError(getErrorMessage(error));
    }
  }, [token, onError]);

  const handleVerify = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const verify = isOtp ? otpVerify : totpVerify;
      const response = await verify({ code, token });

      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error(Constants.genericError);

      onAuthenticated(response);
    } catch (error) {
      setCode('');
      onError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [code, isOtp, token, onAuthenticated, onError]);

  return (
    <>
      <Title>{isOtp ? Constants.otpTitle : Constants.totpTitle}</Title>
      <SectionDialog>
        {isOtp ? Constants.otpInstruction : Constants.totpInstruction}
      </SectionDialog>

      <Span>{Constants.codeHint}</Span>
      <OTPInput length={Constants.codeLength} value={code} onChange={setCode} />

      {isOtp &&
        (timeLeft > 0 ? (
          <>
            <Typography fontSize="14px" mt="24px">
              {Constants.newCodeIn}
            </Typography>
            <Counter>
              {IconTimerMS} {timeLeft} {Constants.seconds}
            </Counter>
          </>
        ) : (
          <NewCodeButton type="button" onClick={handleResend}>
            {Constants.requestNewCode}
          </NewCodeButton>
        ))}

      <ButtonBox>
        <FilledButton
          onClick={handleVerify}
          disabled={code.length < Constants.codeLength || loading}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#FFF' }} />
          ) : (
            Constants.verify
          )}
        </FilledButton>
        <OutlinedStyledButton onClick={onCancel}>
          {Constants.cancel}
        </OutlinedStyledButton>
      </ButtonBox>
    </>
  );
}
